import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  writeBatch,
  serverTimestamp,
  increment,
  limitToLast,
  getDocs,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { monitored } from "../../utils/queryMonitor";
import User from "./user";

export class ChatService {
  /**
   * Listen to all chats for a specific user
   * @param {string} userId
   * @param {(chats: any[]) => void} callback
   * @param {(error: Error) => void} [onError]
   */
  static listenToUserChats(userId, callback, onError) {
    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("uids", "array-contains", userId),
      orderBy("dateUpdated", "desc")
    );

    // Without the error arg a dropped connection fails silently and the UI
    // spins forever (only an app restart recovered). Surface it so the hook can
    // show a retry instead.
    return onSnapshot(
      q,
      (snapshot) => {
        const chats = [];
        snapshot.forEach((doc) => {
          chats.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        callback(chats);
      },
      (error) => {
        console.error("listenToUserChats failed:", error);
        onError?.(error);
      }
    );
  }

  /**
   * Listen to messages for a specific chat
   * @param {string} chatId
   * @param {number} limitCount
   * @param {(messages: any[]) => void} callback
   * @param {(error: Error) => void} [onError]
   */
  static listenToChatMessages(chatId, limitCount, callback, onError) {
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(
      messagesRef,
      orderBy("timestamp", "asc"),
      limitToLast(limitCount)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const messages = [];
        snapshot.forEach((doc) => {
          messages.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        callback(messages);
      },
      (error) => {
        console.error("listenToChatMessages failed:", error);
        onError?.(error);
      }
    );
  }

  /**
   * Get user information by ID
   */
  static async getUserById(userId) {
    try {
      // Users are Supabase-backed now; reading Firestore here showed migrated
      // users (who have no Firestore doc) as "Unknown User". ByIds reads the
      // public row via the same path the profile page uses.
      const [user] = await User.ByIds([userId]);
      if (!user) return null;
      return {
        uid: userId,
        displayName: user.name || "Unknown User",
        emoji: user.emoji,
        supporter: user.supporter,
        incognito: user.incognito,
        deleted: user.deleted,
        lastTugged: user.dates?.lastTugged,
      };
    } catch (error) {
      console.error("Error fetching user:", error);
      return null;
    }
  }

  /**
   * Mark chat as read for a specific user
   */
  static async markChatAsRead(chatId, userId) {
    try {
      const chatRef = doc(db, "chats", chatId);
      await updateDoc(chatRef, {
        [`unreadCount.${userId}`]: 0,
      });
    } catch (error) {
      console.error("Error marking chat as read:", error);
      throw error;
    }
  }

  /**
   * Send a message to a chat
   */
  static async sendMessage(chatId, messageText, senderId, otherUserId) {
    try {
      const batch = writeBatch(db);

      // Add message to subcollection
      const messagesRef = collection(db, "chats", chatId, "messages");
      const messageData = {
        message: messageText,
        senderId,
        timestamp: serverTimestamp(),
      };

      const messageRef = doc(messagesRef);
      batch.set(messageRef, messageData);

      // Update chat metadata
      const chatRef = doc(db, "chats", chatId);
      batch.update(chatRef, {
        dateUpdated: serverTimestamp(),
        lastMessage: {
          text: messageText,
          senderId,
          timestamp: serverTimestamp(),
        },
        length: increment(1),
        [`unreadCount.${otherUserId}`]: increment(1),
      });

      await batch.commit();
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  }

  /**
   * Listen to total unread message count for a user across all chats
   * @param {string} userId - The user's ID
   * @param {(count: number) => void} callback - Callback that receives the total unread count
   * @param {(error: Error) => void} [onError]
   * @returns {function} Unsubscribe function
   */
  static listenToUnreadCount(userId, callback, onError) {
    const chatsRef = collection(db, "chats");
    const q = query(chatsRef, where("uids", "array-contains", userId));

    return onSnapshot(
      q,
      (snapshot) => {
        let totalUnread = 0;
        snapshot.forEach((doc) => {
          const data = doc.data();
          const unreadForUser = data.unreadCount?.[userId] || 0;
          totalUnread += unreadForUser;
        });
        callback(totalUnread);
      },
      (error) => {
        console.error("listenToUnreadCount failed:", error);
        onError?.(error);
      }
    );
  }

  /**
   * Create a new chat between two users
   */
  static async createChat(userId1, userId2) {
    try {
      const chatsRef = collection(db, "chats");

      // Check for existing chat first
      const q = query(chatsRef, where("uids", "array-contains", userId1));
      const querySnapshot = await monitored("chat: find existing", () =>
        getDocs(q)
      );

      const existingChat = querySnapshot.docs.find((doc) => {
        const data = doc.data();
        return data.uids.includes(userId2);
      });

      if (existingChat) {
        return existingChat.id;
      }

      const chatData = {
        uids: [userId1, userId2],
        dateUpdated: serverTimestamp(),
        lastMessage: {
          text: "",
          senderId: "",
          timestamp: serverTimestamp(),
        },
        length: 0,
        unreadCount: {
          [userId1]: 0,
          [userId2]: 0,
        },
      };

      const docRef = await addDoc(chatsRef, chatData);
      return docRef.id;
    } catch (error) {
      console.error("Error creating chat:", error);
      throw error;
    }
  }
}
